<!--
  SectionLayout: the shell of one section (spec 49 section 4.1).

  A section is a rail entry that owns several pages: Team is Drivers,
  Expiring, Schedule, Settlements and Users; Settings is Integrations,
  Devices, Preferences and so on. This component is the parent route's
  component for each of them. It draws the section's one LwPageHeader (the
  page's one <h1>), the row of section tabs under it, and the <router-view>
  the selected tab's page renders into. The page under the tab has no header
  of its own any more; its title is the tab's label, the line under the
  tabs is the tab's `subtitle` from sections.js, and its header buttons
  reach the chassis band through LwSectionActions, which teleports into the
  host element this layout provides.

  WHICH TABS SHOW is the same rule the rail applies (lib/roles.js): a tab
  with `roles` is for exactly those roles. A tab hidden from the current role
  is not unreachable -- typing its URL still renders the page, which carries
  its own "this is for administrators" notice -- it is just not offered.

  THE BARE SECTION PATH (/team, /settings) renders this layout with nothing
  in the router-view, because no tab has an empty path there. Rather than a
  static redirect in the route table, which cannot know the role, the layout
  replaces the location with the first tab the person can see, query and
  hash kept. Sections whose first tab IS the bare path (Customers, Equipment)
  never take this branch.
-->
<template>
  <div class="lw-c-section" :data-section="section.key">
    <LwPageHeader :title="section.title">
      <template #actions>
        <div ref="host" class="lw-c-section__actions" data-test="section-actions" />
      </template>
    </LwPageHeader>
    <!--
      data-tour: the tab row, keyed by the section once one of its tabs is
      open (not on the bare path, which is about to move to the first tab),
      so a walkthrough that clicks a section in the rail waits for where it
      lands (tours/definitions/can-we-take-it.js).
    -->
    <LwSectionTabs
      v-if="tabs.length"
      class="lw-c-section__tabs"
      :label="`${section.title} sections`"
      :tabs="tabs"
      data-tour="section.tabs"
      :data-tour-key="onTab ? section.key : null"
    />
    <!--
      The tab's one-line description (sections.js `subtitle`), under the tab
      row it describes. It sat in the header until 2026-11-03, above the tabs
      and in white on the chassis; the header holds the section's name only,
      so every section's bar is the same height as the rail's user block.
    -->
    <p v-if="subtitle" class="lw-c-section__intro" data-test="section-intro">
      {{ subtitle }}
    </p>
    <router-view />
  </div>
</template>

<script>
import { provide, shallowRef } from 'vue';
import LwPageHeader from '../lw/LwPageHeader.vue';
import LwSectionTabs from '../lw/LwSectionTabs.vue';
import { findSection } from '../../router/sections.js';
import { currentRole, visibleTabs } from '../../lib/roles.js';

export default {
  name: 'SectionLayout',
  components: { LwPageHeader, LwSectionTabs },
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
  },
  setup() {
    // The header's actions slot, handed to LwSectionActions as a ref so a
    // page mounted before this element exists still finds it once it does.
    const host = shallowRef(null);
    provide('lwSectionActionsHost', host);
    // The tabs above the page make any tab row inside it the inner kind
    // (LwTabs draws it as segmented pills).
    provide('lwTabsNested', true);
    return { host };
  },
  computed: {
    section() {
      return findSection(this.sectionKey) || { key: this.sectionKey, title: '', tabs: [] };
    },
    role() {
      return currentRole(this.$store);
    },
    tabs() {
      return visibleTabs(this.section, this.role).map(tab => ({
        name: tab.name,
        label: tab.label,
      }));
    },
    onTab() {
      return this.section.tabs.some(tab => tab.name === this.$route.name);
    },
    subtitle() {
      return (this.$route.meta && this.$route.meta.subtitle) || null;
    },
  },
  watch: {
    '$route.name': {
      immediate: true,
      handler() {
        this.landOnFirstTab();
      },
    },
    // The role arrives after /users/me answers; a person who opened /settings
    // cold must land on a tab they can see once it is known.
    role() {
      this.landOnFirstTab();
    },
  },
  methods: {
    landOnFirstTab() {
      if (this.onTab || !this.$router) return;
      const first = this.tabs[0];
      if (!first) return;
      this.$router.replace({
        name: first.name,
        query: this.$route.query,
        hash: this.$route.hash,
      });
    },
  },
};
</script>

<style scoped lang="scss">
// The row sits a gutter under the bar, where Billing's and Dispatch's do
// (their page body's top padding).
.lw-c-section__tabs {
  margin-top: var(--lw-space-4);
}

.lw-c-section__intro {
  margin: var(--lw-space-4) 0 0;
  max-width: var(--lw-layout-max-prose);
  font: var(--lw-type-body);
  color: var(--lw-fg-secondary);
}

.lw-c-section__actions {
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
}
</style>
